/**
 * 觀看進度對話框組件
 * 顯示影片的觀看進度，並提供恢復播放的選項
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import WatchProgressManager from '../utils/watchProgressManager';

interface WatchProgressDialogProps {
  open: boolean;
  onClose: () => void;
  videoId: string;
  videoTitle?: string;
  progress: {
    currentTime: number;
    duration: number;
    progress: number;
    lastWatched: string;
    watchCount: number;
    isCompleted: boolean;
  };
  onRestoreProgress: (time: number) => void;
  onClearProgress: () => void;
  onStartFromBeginning: () => void;
}

const WatchProgressDialog: React.FC<WatchProgressDialogProps> = ({
  open,
  onClose,
  videoId,
  videoTitle,
  progress,
  onRestoreProgress,
  onClearProgress,
  onStartFromBeginning
}) => {
  const handleRestoreProgress = () => {
    onRestoreProgress(progress.currentTime);
    onClose();
  };

  const handleStartFromBeginning = () => {
    onStartFromBeginning();
    onClose();
  };

  const handleClearProgress = () => {
    if (window.confirm('確定要清除觀看進度嗎？此操作無法復原。')) {
      onClearProgress();
      onClose();
    }
  };

  const formatLastWatched = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return '今天';
      } else if (diffDays === 1) {
        return '昨天';
      } else if (diffDays < 7) {
        return `${diffDays}天前`;
      } else {
        return date.toLocaleDateString();
      }
    } catch {
      return '未知時間';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          <Typography variant="h6">觀看進度</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          {videoTitle && (
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              {videoTitle}
            </Typography>
          )}
          
          {/* 進度條 */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                {WatchProgressManager.formatTime(progress.currentTime)}
              </Typography>
              <Typography variant="body2">
                {WatchProgressManager.formatTime(progress.duration)}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress.progress * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#4caf50'
                }
              }}
            />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              觀看進度: {Math.round(progress.progress * 100)}%
            </Typography>
          </Box>
          
          {/* 統計信息 */}
          <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Chip 
              icon={<HistoryIcon />}
              label={`觀看 ${progress.watchCount} 次`}
              variant="outlined"
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }}
            />
            <Chip 
              label={`上次觀看: ${formatLastWatched(progress.lastWatched)}`}
              variant="outlined"
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                backgroundColor: 'rgba(255,255,255,0.1)'
              }}
            />
            {progress.isCompleted && (
              <Chip 
                label="已完成"
                color="success"
                variant="filled"
                sx={{ backgroundColor: '#4caf50' }}
              />
            )}
          </Stack>
          
          {/* 恢復提示 */}
          <Box 
            sx={{ 
              p: 2, 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              borderRadius: 1,
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              檢測到您上次觀看至此處，是否要從上次停止的地方繼續播放？
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Tooltip title="清除觀看進度">
          <IconButton 
            onClick={handleClearProgress}
            sx={{ color: 'white' }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        
        <Box sx={{ flex: 1 }} />
        
        <Button 
          onClick={handleStartFromBeginning}
          variant="outlined"
          sx={{ 
            color: 'white', 
            borderColor: 'rgba(255,255,255,0.5)',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          從頭開始
        </Button>
        
        <Button 
          onClick={handleRestoreProgress}
          variant="contained"
          startIcon={<RestoreIcon />}
          sx={{ 
            backgroundColor: '#4caf50',
            '&:hover': {
              backgroundColor: '#45a049'
            }
          }}
        >
          繼續觀看
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WatchProgressDialog;
